import is from '@sindresorhus/is';
import { z } from 'zod';

export const JSA_PRIMARY_DETAIL_TILE_SLOT_KEY = 'jsa.primary-detail';
export const JSA_COMPARE_TILE_SLOT_KEY = 'jsa.compare';
export const JSA_CHANGELOG_TILE_SLOT_KEY = 'jsa.changelog';
export const JSA_ARCHIVE_TILE_SLOT_KEY = 'jsa.archive';
export const JSA_PROFILE_DETAIL_TILE_TYPE = 'v1.jsa.profile.detail';
export const JSA_FAMILY_DETAIL_TILE_TYPE = 'v1.jsa.family.detail';
export const JSA_CLUSTER_DETAIL_TILE_TYPE = 'v1.jsa.cluster.detail';
export const JSA_COMPARE_TILE_TYPE = 'v1.jsa.compare';
export const JSA_CHANGELOG_TILE_TYPE = 'v1.jsa.changelog';
export const JSA_ARCHIVE_TILE_TYPE = 'v1.jsa.archive';
export const JSA_CHANGELOG_TILE_WIDTH = '400px';
export const JSA_ARCHIVE_TILE_MAX_WIDTH = '800px';

const jsaItemTypeSchema = z.enum(['job-level', 'job-role']);
const jsaModeSchema = z.enum(['edit', 'view']);

export const jsaProfileDetailTileSchema = z.object({
    architectureId: z.uuid(),
    isArchived: z.boolean().optional(),
    isCommentsOpen: z.boolean().optional(),
    itemType: jsaItemTypeSchema,
    mode: jsaModeSchema,
    preferredLanguage: z.string().optional(),
    profileId: z.uuid(),
    type: z.literal(JSA_PROFILE_DETAIL_TILE_TYPE),
    version: z.number().int().positive(),
});

export const jsaFamilyDetailTileSchema = z.object({
    architectureId: z.uuid(),
    familyId: z.uuid(),
    mode: jsaModeSchema,
    type: z.literal(JSA_FAMILY_DETAIL_TILE_TYPE),
});

export const jsaClusterDetailTileSchema = z.object({
    architectureId: z.uuid(),
    clusterId: z.uuid(),
    familyId: z.uuid(),
    mode: jsaModeSchema,
    type: z.literal(JSA_CLUSTER_DETAIL_TILE_TYPE),
});

export const jsaCompareTileSchema = z.object({
    architectureId: z.uuid(),
    itemType: jsaItemTypeSchema,
    preferredLanguage: z.string().optional(),
    profileId: z.uuid(),
    type: z.literal(JSA_COMPARE_TILE_TYPE),
    versionA: z.string().min(1),
    versionB: z.string().min(1),
});

export const jsaChangelogTileSchema = z.object({
    architectureId: z.uuid(),
    type: z.literal(JSA_CHANGELOG_TILE_TYPE),
});

export const jsaArchiveTileSchema = z.object({
    architectureId: z.uuid(),
    type: z.literal(JSA_ARCHIVE_TILE_TYPE),
});

export type JsaProfileDetailTilePayload = z.infer<typeof jsaProfileDetailTileSchema>;
export type JsaFamilyDetailTilePayload = z.infer<typeof jsaFamilyDetailTileSchema>;
export type JsaClusterDetailTilePayload = z.infer<typeof jsaClusterDetailTileSchema>;
export type JsaCompareTilePayload = z.infer<typeof jsaCompareTileSchema>;
export type JsaChangelogTilePayload = z.infer<typeof jsaChangelogTileSchema>;
export type JsaArchiveTilePayload = z.infer<typeof jsaArchiveTileSchema>;
export type JsaPrimaryDetailTilePayload =
    | JsaClusterDetailTilePayload
    | JsaFamilyDetailTilePayload
    | JsaProfileDetailTilePayload;

export const versionTokenToNumber = (token: string | undefined): number | undefined => {
    if (!is.nonEmptyString(token) || !token.startsWith('v')) {
        return undefined;
    }

    const version = Number.parseInt(token.slice(1), 10);

    return Number.isNaN(version) ? undefined : version;
};

export const versionTokenToString = (token: string | undefined): string | undefined => {
    if (!is.nonEmptyString(token) || !token.startsWith('v')) {
        return undefined;
    }

    const version = token.slice(1);

    return is.nonEmptyString(version) ? version : undefined;
};

export const createJsaProfileDetailTilePayload = ({
    architectureId,
    isArchived,
    isCommentsOpen,
    itemType,
    mode,
    preferredLanguage,
    profileId,
    version = 1,
}: {
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly itemType: 'job-level' | 'job-role';
    readonly mode: 'edit' | 'view';
    readonly preferredLanguage?: string;
    readonly profileId: string;
    readonly version?: number;
}): JsaProfileDetailTilePayload => ({
    architectureId,
    itemType,
    mode,
    ...(!is.undefined(isArchived) ? { isArchived } : {}),
    ...(!is.undefined(isCommentsOpen) ? { isCommentsOpen } : {}),
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    profileId,
    type: JSA_PROFILE_DETAIL_TILE_TYPE,
    version,
});

export const createJsaFamilyDetailTilePayload = ({
    architectureId,
    familyId,
    mode,
}: {
    readonly architectureId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
}): JsaFamilyDetailTilePayload => ({
    architectureId,
    familyId,
    mode,
    type: JSA_FAMILY_DETAIL_TILE_TYPE,
});

export const createJsaClusterDetailTilePayload = ({
    architectureId,
    clusterId,
    familyId,
    mode,
}: {
    readonly architectureId: string;
    readonly clusterId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
}): JsaClusterDetailTilePayload => ({
    architectureId,
    clusterId,
    familyId,
    mode,
    type: JSA_CLUSTER_DETAIL_TILE_TYPE,
});

export const createJsaCompareTilePayload = ({
    architectureId,
    itemType,
    preferredLanguage,
    profileId,
    versionA,
    versionB,
}: {
    readonly architectureId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly preferredLanguage?: string;
    readonly profileId: string;
    readonly versionA: string;
    readonly versionB: string;
}): JsaCompareTilePayload => ({
    architectureId,
    itemType,
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    profileId,
    type: JSA_COMPARE_TILE_TYPE,
    versionA,
    versionB,
});

export const createJsaChangelogTilePayload = ({
    architectureId,
}: {
    readonly architectureId: string;
}): JsaChangelogTilePayload => ({
    architectureId,
    type: JSA_CHANGELOG_TILE_TYPE,
});

export const createJsaArchiveTilePayload = ({
    architectureId,
}: {
    readonly architectureId: string;
}): JsaArchiveTilePayload => ({
    architectureId,
    type: JSA_ARCHIVE_TILE_TYPE,
});

export const isJsaProfileDetailTilePayload = (payload: unknown): payload is JsaProfileDetailTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_PROFILE_DETAIL_TILE_TYPE;

export const isJsaCompareTilePayload = (payload: unknown): payload is JsaCompareTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_COMPARE_TILE_TYPE;
